const STORAGE_KEY = 'molish-dashboard-locale';

/**
 * Query-string param used by the marketing site (and any cross-app link) to seed the
 * dashboard's locale on first load. Lets a visitor's language choice on `molish.com` carry
 * over to `app.molish.com`, which lives on a different subdomain and cannot share localStorage.
 */
export const LOCALE_QUERY_PARAM = 'lang';

export type DashboardLocale = 'en' | 'el';

export const DASHBOARD_LOCALES: DashboardLocale[] = ['en', 'el'];

export function readStoredLocale(): DashboardLocale | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'en' || raw === 'el') {
      return raw;
    }
  } catch {
    // ignore
  }
  return null;
}

export function writeStoredLocale(locale: DashboardLocale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // ignore
  }
}

/** Read a supported locale from `?lang=…` on the current URL, if present. */
export function readQueryParamLocale(): DashboardLocale | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = new URLSearchParams(window.location.search).get(LOCALE_QUERY_PARAM);
    if (raw === 'en' || raw === 'el') {
      return raw;
    }
  } catch {
    // ignore
  }
  return null;
}

/** BCP 47 primary language; falls back to English when unavailable or unsupported. */
export function getBrowserPrimaryLanguage(): DashboardLocale {
  if (typeof navigator === 'undefined') {
    return 'en';
  }
  const tag = navigator.language || (navigator.languages && navigator.languages[0]) || 'en';
  const primary = tag.split('-')[0]?.toLowerCase() || 'en';
  if (primary === 'el') {
    return 'el';
  }
  return 'en';
}

/**
 * Resolution order: `?lang=` query → localStorage → browser language → 'en'.
 * When `?lang=` wins, the value is also written to localStorage so subsequent visits
 * (without the param) keep the chosen locale.
 */
export function getInitialDashboardLocale(): DashboardLocale {
  const fromQuery = readQueryParamLocale();
  if (fromQuery) {
    writeStoredLocale(fromQuery);
    return fromQuery;
  }
  return readStoredLocale() ?? getBrowserPrimaryLanguage();
}
